import { useCallback, useEffect, useState } from 'react'

// Light / dark theme, persisted to localStorage and applied via <html data-theme>.
// The very first value is set by an inline script in index.html (before paint, so
// there's no flash); this hook reads that value back and owns subsequent changes.

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'whimer-theme'

function readInitial(): Theme {
  const attr = document.documentElement.getAttribute('data-theme')
  if (attr === 'light' || attr === 'dark') return attr
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return 'dark' // default to dark when the user hasn't chosen (ignore OS preference)
}

function apply(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', theme === 'light' ? '#ffffff' : '#171717')
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readInitial)

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    apply(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* private mode: ignore */ }
  }, [])

  const toggle = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }, [theme, setTheme])

  // Keep the DOM in sync if state ever changes without going through setTheme.
  useEffect(() => { apply(theme) }, [theme])

  return { theme, toggle, setTheme }
}
